import { z } from 'zod'

import {
  expenseCategorySchema,
  idSchema,
  isoDateSchema,
  labelSchema,
  moneyPaisa,
  ratioSchema,
  sortSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * A43–A47 · Reports. Every one of these needs the money capability **and** the
 * `REPORTS` plan feature. The one exception is `/reports/activity`, the month
 * in counts with no amount in it, which is the Reports tab a Staff account
 * gets — the owner's decision of 11 Sept 2026.
 */

/** The `?from&to&seasonTag` every report shares. A season beats a date range. */
export const reportRangeQuery = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  seasonTag: z.string().trim().max(40).optional(),
})

/* ── A43 · GET /reports/summary ───────────────────────────────────────────── */

/**
 * §4.19, BR §14 · **The two P&L numbers, and they will not reconcile.**
 *
 * `profitBookedPaisa` is the matching basis: a sold animal's costs follow it to
 * the month it sold, so the figure answers "did these deals make money".
 * `cashSpentPaisa` is the cash basis: what left the pocket in the window,
 * including feed for animals still standing in the shed. They differ by every
 * cost still sitting in unsold inventory.
 *
 * Both are returned, both are labelled, and both are shown — because he *will*
 * ask why they differ, and a single blended number would be a wrong answer to
 * whichever question he was actually asking. `explanation` is the one-line
 * reason, produced server-side so it stays consistent with the arithmetic.
 */
export const reportSummaryResponse = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  asOf: isoDateSchema,
  seasonTag: z.string().nullish(),
  /** Matching basis: proceeds less the cost basis of what was actually sold. */
  profitBookedPaisa: moneyPaisa(),
  /** Cash basis: every rupee that left the pocket in the window. */
  cashSpentPaisa: moneyPaisa(),
  /** Why they differ, in one sentence. Never omitted when either figure is shown. */
  explanation: z.string(),
  animalsSold: z.number().int().nonnegative(),
  animalsHeld: z.number().int().nonnegative(),
  averageDaysHeld: z.number().int().nonnegative(),
  revenuePaisa: moneyPaisa(),
  costOfSalesPaisa: moneyPaisa(),
  moneyTiedUpPaisa: moneyPaisa(),
  receivableTotalPaisa: moneyPaisa(),
  /** Best trades of the window, ranked by profit per day (§4.10). */
  bestDeals: z.array(
    z.object({
      animalId: idSchema,
      animalLabel: z.string(),
      daysHeld: z.number().int().nonnegative(),
      profitPaisa: moneyPaisa(),
      profitPerDayPaisa: moneyPaisa(),
    }),
  ),
})

/* ── A44 · GET /reports/per-animal ────────────────────────────────────────── */

/**
 * Sorted by **profit per day** by default. Rs 32,703 over 90 days is a worse
 * trade than Rs 25,000 over 30, and every list that ranks by headline profit
 * teaches the trader the wrong lesson.
 */
export const PER_ANIMAL_SORTS = [
  '-profitPerDay',
  'profitPerDay',
  '-profit',
  '-daysHeld',
  'soldOn',
] as const

export const perAnimalReportQuery = cursorQuerySchema.extend({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  seasonTag: z.string().trim().max(40).optional(),
  sort: sortSchema(PER_ANIMAL_SORTS, '-profitPerDay'),
})

export const perAnimalReportRowSchema = z.object({
  animalId: idSchema,
  animalLabel: z.string(),
  purchaseDate: isoDateSchema,
  soldOn: isoDateSchema.nullish(),
  daysHeld: z.number().int().nonnegative(),
  purchasePricePaisa: moneyPaisa(),
  costBasisPaisa: moneyPaisa(),
  salePricePaisa: moneyPaisa(),
  profitPaisa: moneyPaisa(),
  profitPerDayPaisa: moneyPaisa(),
  roiPercent: ratioSchema.optional(),
  annualisedRoiPercent: ratioSchema.optional(),
})

export const perAnimalReportResponse = z.array(perAnimalReportRowSchema)

/* ── A45 · GET /reports/cost-breakdown ────────────────────────────────────── */

/** "Where the money goes" — the bars on screen 15. */
export const costBreakdownReportResponse = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  totalPaisa: moneyPaisa(),
  bars: z.array(
    z.object({
      category: expenseCategorySchema,
      label: labelSchema,
      amountPaisa: moneyPaisa(),
      /** 0–1. Sent even when the amount is not, so the shape still reads. */
      share: ratioSchema,
    }),
  ),
  /** Feed split the two ways BR §6 insists on: what was estimated, what was billed. */
  feedEstimatePaisa: moneyPaisa(),
  feedActualPaisa: moneyPaisa(),
})

/* ── A46 · GET /reports/ageing ────────────────────────────────────────────── */

/**
 * Two ageings on one screen: how long the animals have been standing, and how
 * long the money has been owed. Both are the same question — capital that is
 * not moving.
 */
export const ageingReportResponse = z.object({
  asOf: isoDateSchema,
  inventory: z.array(
    z.object({
      bucket: z.enum(['0-30', '31-60', '61-90', '90+']),
      count: z.number().int().nonnegative(),
      costBasisPaisa: moneyPaisa(),
    }),
  ),
  receivables: z.array(
    z.object({
      bucket: z.enum(['0-30', '31-60', '61-90', '90+']),
      count: z.number().int().nonnegative(),
      amountPaisa: moneyPaisa(),
    }),
  ),
  oldestHeld: z
    .array(
      z.object({
        animalId: idSchema,
        animalLabel: z.string(),
        daysHeld: z.number().int().nonnegative(),
        costBasisPaisa: moneyPaisa(),
      }),
    )
    .optional(),
})

/* ── A43s · GET /reports/activity ─────────────────────────────────────────── */

/**
 * The month in COUNTS — the Reports tab a Staff account gets.
 *
 * The owner asked for Staff to have a Reports tab (11 Sept 2026) and chose the
 * version with no money in it. So this payload has **no `...Paisa` field at
 * all**: not gated, not stripped — there is nothing to strip. It is the work a
 * munshi did and can check: what came in, what went out, what died, what is
 * still standing and for how long, and how many bills were written down.
 *
 * Served to every role (an owner may open it too); `REPORTS` plan feature only.
 */
export const reportActivityResponse = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  asOf: isoDateSchema,
  /** Purchased inside the window. */
  bought: z.number().int().nonnegative(),
  /** Left by a sale inside the window. */
  sold: z.number().int().nonnegative(),
  /** Died or lost inside the window — never counted as a sale. */
  died: z.number().int().nonnegative(),
  /** Standing in the shed today (AVAILABLE or RESERVED). */
  inStock: z.number().int().nonnegative(),
  /** Average hold of the animals sold in the window; 0 when none were. */
  averageDaysHeld: z.number().int().nonnegative(),
  /** Expense entries dated inside the window. A count, never an amount. */
  expenseEntries: z.number().int().nonnegative(),
  /** The animals sold in the window, latest first. */
  soldAnimals: z.array(
    z.object({
      animalId: idSchema,
      animalLabel: z.string(),
      soldOn: isoDateSchema,
      daysHeld: z.number().int().nonnegative(),
    }),
  ),
  /** The longest-standing animals still held, longest first. */
  heldLongest: z.array(
    z.object({
      animalId: idSchema,
      animalLabel: z.string(),
      daysHeld: z.number().int().nonnegative(),
    }),
  ),
})

/* ── A47 · GET /reports/export ────────────────────────────────────────────── */

/**
 * Streams a file rather than returning JSON, and the download itself is
 * audited: an export is the whole book leaving the building.
 */
export const reportExportQuery = z.object({
  format: z.enum(['xlsx', 'csv']),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  seasonTag: z.string().trim().max(40).optional(),
  dataset: z.enum(['animals', 'expenses', 'sales', 'everything']).default('everything'),
})

/* ── A48–A49 · PDF reports ────────────────────────────────────────────────── */

/**
 * `GET /reports/monthly.pdf?month=YYYY-MM` and `GET /reports/animals/:animalId/pdf`.
 * Both answer with `application/pdf` bytes, not the `{ data }` envelope, and
 * both are audited like the export.
 */
export const monthlyReportPdfQuery = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a month like 2026-09.'),
})

export const animalReportPdfParams = z.object({ animalId: idSchema })

export type MonthlyReportPdfQuery = z.input<typeof monthlyReportPdfQuery>

export type ReportRangeQuery = z.input<typeof reportRangeQuery>
export type ReportSummaryResponse = z.infer<typeof reportSummaryResponse>
export type ReportActivityResponse = z.infer<typeof reportActivityResponse>
export type PerAnimalReportQuery = z.input<typeof perAnimalReportQuery>
export type PerAnimalReportRow = z.infer<typeof perAnimalReportRowSchema>
export type CostBreakdownReportResponse = z.infer<typeof costBreakdownReportResponse>
export type AgeingReportResponse = z.infer<typeof ageingReportResponse>
export type ReportExportQuery = z.input<typeof reportExportQuery>
